// @ts-nocheck
/**
 * Standalone Store Locator Module
 * 
 * This module operates independently and hooks into existing metaobjects-list
 * without requiring any modifications to that file.
 * 
 * Features:
 * - Auto-detects store location pages
 * - Uses stored latitude/longitude from metaobject fields
 * - ZIP code to coordinates conversion  
 * - Distance calculation and filtering
 * - Configurable result count and radius
 */

class StandaloneStoreLocator {
  constructor() {
    this.metaobjectsListElement = null;
    this.allLocations = [];
    this.userLocation = null;
    
    // Settings
    this.currentZipCode = '';
    this.maxResults = 10;
    this.maxDistance = 25;
    
    // Cache for ZIP codes
    this.zipCodeCache = new Map();
    
    // Original functions we'll need to restore
    this.originalRenderFunction = null;
    this.originalApplyFiltersFunction = null;
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.detectAndInitialize());
    } else {
      this.detectAndInitialize();
    }
  }

  detectAndInitialize() {
    // Look for metaobjects-list element
    this.metaobjectsListElement = document.querySelector('metaobjects-list');
    
    if (!this.metaobjectsListElement) {
      console.warn('🔍 No metaobjects-list found, Store Locator not initialized');
      return;
    }

    // Check if this is a store locator page
    const metaobjectType = this.metaobjectsListElement.dataset.metaobjectType || '';
    if (!metaobjectType.toLowerCase().includes('store')) {
      console.warn('🔍 Not a store page, Store Locator not initialized');
      return;
    }
    
    // Wait for the metaobjects to be loaded
    this.waitForMetaobjectsToLoad();
  }

  waitForMetaobjectsToLoad() {
    const checkInterval = setInterval(() => {
      // Check if metaobjects have been loaded
      const gridElement = this.metaobjectsListElement.querySelector('.metaobjects-grid');
      const hasItems = gridElement && gridElement.children.length > 0;
      
      if (hasItems) {
        clearInterval(checkInterval);
        console.warn('✅ Metaobjects loaded, initializing Store Locator');
        this.setupStoreLocator();
      }
    }, 500);

    // Stop checking after 10 seconds
    setTimeout(() => {
      clearInterval(checkInterval);
    }, 10000);
  }

  setupStoreLocator() {
    // Check if already initialized to prevent duplicates
    if (this.metaobjectsListElement.querySelector('.store-locator-section')) {
      console.warn('🔄 Store Locator already initialized, skipping');
      return;
    }
    
    // Get reference to the metaobjects list instance
    this.getMetaobjectsData();
    
    // Hide latitude/longitude fields from display
    this.hideCoordinateFields();
    
    // Create the store locator UI
    this.createStoreLocatorUI();
    
    // Attach event listeners
    this.attachEventListeners();
    
    // Hook into the existing rendering system
    this.hookIntoRenderSystem();
  }

  getMetaobjectsData() {
    // Try to access the data from the custom element
    // This is a bit hacky but works without modifying the original file
    const customElement = this.metaobjectsListElement;
    
    // Look for the data in various possible locations
    if (customElement.allMetaobjects) {
      this.allLocations = customElement.allMetaobjects;
    } else {
      // Fallback: extract from DOM
      this.extractLocationsFromDOM();
    }
    
  }

  extractLocationsFromDOM() {
    // As a fallback, we can try to extract location data from the DOM
    // This is less ideal but works if we can't access the JS data directly
    const items = this.metaobjectsListElement.querySelectorAll('.metaobject-item');
    this.allLocations = [];
    
    items.forEach((item, index) => {
      // Extract what we can from the DOM
      const titleEl = item.querySelector('h3');
      const title = titleEl ? titleEl.textContent : `Location ${index + 1}`;
      
      // Create a basic location object
      this.allLocations.push({
        id: `location-${index}`,
        handle: title.toLowerCase().replace(/\s+/g, '-'),
        field: { value: title },
        fields: [], // We'll need the coordinates to be in the metaobject fields
        domElement: item
      });
    });
  }

  createStoreLocatorUI() {
    const controlsContainer = this.metaobjectsListElement.querySelector('.metaobjects-controls');
    if (!controlsContainer) {
      // Create controls container if it doesn't exist
      const container = this.metaobjectsListElement.querySelector('.metaobjects-container');
      if (container) {
        const controlsHTML = `<div class="metaobjects-controls"></div>`;
        container.insertAdjacentHTML('afterbegin', controlsHTML);
      }
    }

    const controls = this.metaobjectsListElement.querySelector('.metaobjects-controls');
    if (!controls) return;

    const storeLocatorHTML = `
      <div class="store-locator-section">
        <div class="store-locator-header">
          <p>Locate a store near you by entering your ZIP code below</p>
        </div>
        
        <div class="store-locator-controls">
          <div class="zip-search-group">
            <label for="zip-input">Your ZIP Code:</label>
            <div class="zip-input-wrapper">
              <input 
                type="text" 
                id="zip-input"
                placeholder="Enter ZIP code (e.g., 15317)" 
                class="zip-input"
                maxlength="10"
                pattern="[0-9]{5}(-[0-9]{4})?"
              >
              <button class="zip-search-button" type="button">Search</button>
            </div>
          </div>
          
          <div class="filter-controls">
            <div class="control-item">
              <label for="result-count">Show closest:</label>
              <select id="result-count" class="result-count-select">
                <option value="5">5 Stores</option>
                <option value="10" selected>10 Stores</option>
                <option value="15">15 Stores</option>
                <option value="20">20 Stores</option>
                <option value="25">25 Stores</option>
              </select>
            </div>
            
            <div class="control-item">
              <label for="distance-range">Within:</label>
              <select id="distance-range" class="distance-range-select">
                <option value="5">5 Miles</option>
                <option value="10">10 Miles</option>
                <option value="15">15 Miles</option>
                <option value="20">20 Miles</option>
                <option value="25" selected>25 Miles</option>
                <option value="50">50 Miles</option>
                <option value="75">75 Miles</option>
                <option value="100">100 Miles</option>
              </select>
            </div>
          </div>
        </div>

        <div class="store-locator-status">
          <div class="status-display" style="display: none;"></div>
          <div class="location-info" style="display: none;"></div>
          <button class="reset-button" style="display: none;" type="button">
            View All Locations
          </button>
        </div>
      </div>
    `;

    // Insert at the beginning of controls
    controls.insertAdjacentHTML('afterbegin', storeLocatorHTML);
  }

  attachEventListeners() {
    // ZIP code search
    const zipInput = this.metaobjectsListElement.querySelector('.zip-input');
    const searchButton = this.metaobjectsListElement.querySelector('.zip-search-button');
    
    if (zipInput && searchButton) {
      searchButton.addEventListener('click', () => this.handleZipSearch());
      zipInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') this.handleZipSearch();
      });
      
      zipInput.addEventListener('input', (e) => this.validateZipInput(e.target));
    }

    // Result count
    const resultSelect = this.metaobjectsListElement.querySelector('.result-count-select');
    if (resultSelect) {
      resultSelect.addEventListener('change', (e) => {
        this.maxResults = parseInt(e.target.value);
        if (this.userLocation) this.applyLocationFilter();
      });
    }

    // Distance range
    const distanceSelect = this.metaobjectsListElement.querySelector('.distance-range-select');
    if (distanceSelect) {
      distanceSelect.addEventListener('change', (e) => {
        this.maxDistance = parseInt(e.target.value);
        if (this.userLocation) this.applyLocationFilter();
      });
    }

    // Reset button
    const resetButton = this.metaobjectsListElement.querySelector('.reset-button');
    if (resetButton) {
      resetButton.addEventListener('click', () => this.resetToAllLocations());
    }
  }

  validateZipInput(input) {
    const value = input.value.replace(/\D/g, '');
    const isValid = /^\d{5}$/.test(value);
    
    input.classList.toggle('invalid', !isValid && value.length > 0);
    
    const searchBtn = this.metaobjectsListElement.querySelector('.zip-search-button');
    if (searchBtn) {
      searchBtn.disabled = !isValid;
    }
    
    return isValid;
  }

  async handleZipSearch() {
    const zipInput = this.metaobjectsListElement.querySelector('.zip-input');
    if (!zipInput) return;

    const zipCode = zipInput.value.trim();
    if (!zipCode || !this.validateZipInput(zipInput)) {
      this.showStatus('Please enter a valid 5-digit ZIP code', 'error');
      return;
    }

    this.currentZipCode = zipCode;
    this.showStatus('Finding stores near ' + zipCode + '...', 'loading');

    try {
      const coordinates = await this.getZipCodeCoordinates(zipCode);
      if (!coordinates) {
        this.showStatus('Unable to find location for ZIP code ' + zipCode, 'error');
        return;
      }

      this.userLocation = coordinates;
      this.applyLocationFilter();
      
    } catch (error) {
      console.error('ZIP search error:', error);
      this.showStatus('Error searching for locations. Please try again.', 'error');
    }
  }

  async getZipCodeCoordinates(zipCode) {
    if (this.zipCodeCache.has(zipCode)) {
      return this.zipCodeCache.get(zipCode);
    }

    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);
      if (!response.ok) throw new Error('ZIP code not found');
      
      const data = await response.json();
      const coordinates = {
        latitude: parseFloat(data.places[0].latitude),
        longitude: parseFloat(data.places[0].longitude),
        city: data.places[0]['place name'],
        state: data.places[0]['state abbreviation']
      };
      
      this.zipCodeCache.set(zipCode, coordinates);
      return coordinates;
      
    } catch (error) {
      console.error('Error fetching ZIP coordinates:', error);
      return null;
    }
  }

  applyLocationFilter() {
    if (!this.userLocation) return;

    this.showStatus('Calculating distances...', 'loading');

    // Calculate distances for all locations
    const locationsWithDistance = this.allLocations
      .map(location => {
        const coords = this.extractCoordinates(location);
        if (!coords) return null;
        
        const distance = this.calculateDistance(
          this.userLocation.latitude,
          this.userLocation.longitude,
          coords.latitude,
          coords.longitude
        );
        
        return {
          ...location,
          distance: distance,
          distanceText: distance.toFixed(1) + ' miles'
        };
      })
      .filter(location => location !== null && location.distance <= this.maxDistance)
      .sort((a, b) => a.distance - b.distance)
      .slice(0, this.maxResults);

    // Update the display
    this.renderFilteredLocations(locationsWithDistance);
    
    // Update status
    const foundCount = locationsWithDistance.length;
    const statusText = foundCount > 0 
      ? `Found ${foundCount} store${foundCount !== 1 ? 's' : ''} within ${this.maxDistance} miles`
      : `No stores found within ${this.maxDistance} miles of ${this.currentZipCode}`;
    
    this.showStatus(statusText, foundCount > 0 ? 'success' : 'warning');
    this.showLocationInfo();
    this.showResetButton(true);
  }

  extractCoordinates(location) {
    // Look for latitude and longitude fields
    if (location.fields) {
      const latField = location.fields.find(f => f.key === 'latitude');
      const lngField = location.fields.find(f => f.key === 'longitude');
      
      if (latField && lngField && latField.value && lngField.value) {
        const lat = parseFloat(latField.value);
        const lng = parseFloat(lngField.value);
        
        if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
          return { latitude: lat, longitude: lng };
        }
      }
    }
    
    return null;
  }

  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const lat1Rad = this.toRad(lat1);
    const lat2Rad = this.toRad(lat2);

    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1Rad) * Math.cos(lat2Rad);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    
    return R * c;
  }

  toRad(value) {
    return value * Math.PI / 180;
  }

  renderFilteredLocations(locations) {
    // This is where we hook into the existing render system
    // We'll trigger the existing metaobjects list to re-render with our filtered data
    
    const customElement = this.metaobjectsListElement;
    
    // Update the filtered data
    if (customElement.filteredMetaobjects !== undefined) {
      customElement.filteredMetaobjects = locations;
    }
    
    // Try to trigger the existing render method
    if (typeof customElement.renderMetaobjects === 'function') {
      customElement.renderMetaobjects(locations);
    } else {
      // Fallback: manually update the DOM
      this.manuallyRenderLocations(locations);
    }
    
    // Add distance badges to the items
    this.addDistanceBadges(locations);
    
    // Re-hide coordinate fields after rendering
    this.hideCoordinateFields();
  }

  manuallyRenderLocations(locations) {
    const grid = this.metaobjectsListElement.querySelector('.metaobjects-grid');
    if (!grid) return;

    // Hide all items first
    const allItems = grid.querySelectorAll('.metaobject-item');
    allItems.forEach(item => item.style.display = 'none');

    // Show only the filtered locations
    locations.forEach(location => {
      if (location.domElement) {
        location.domElement.style.display = 'block';
      }
    });
  }

  addDistanceBadges(locations) {
    locations.forEach(location => {
      if (!location.domElement || !location.distanceText) return;

      // Remove existing distance badge
      const existingBadge = location.domElement.querySelector('.distance-badge');
      if (existingBadge) existingBadge.remove();

      // Add new distance badge
      const badge = document.createElement('div');
      badge.className = 'distance-badge';
      badge.innerHTML = `📍 ${location.distanceText} away`;

      const titleEl = location.domElement.querySelector('h3');
      if (titleEl) {
        titleEl.insertAdjacentElement('afterend', badge);
      }
    });
  }

  showStatus(message, type = 'info') {
    const statusEl = this.metaobjectsListElement.querySelector('.status-display');
    if (!statusEl) return;

    statusEl.textContent = message;
    statusEl.className = `status-display status-${type}`;
    statusEl.style.display = 'block';

    if (type === 'success') {
      setTimeout(() => statusEl.style.display = 'none', 5000);
    }
  }

  showLocationInfo() {
    if (!this.userLocation) return;

    const infoEl = this.metaobjectsListElement.querySelector('.location-info');
    if (!infoEl) return;

    const locationText = this.userLocation.city && this.userLocation.state
      ? `${this.userLocation.city}, ${this.userLocation.state} (${this.currentZipCode})`
      : this.currentZipCode;

    infoEl.innerHTML = `<strong>Your Location:</strong> ${locationText}`;
    infoEl.style.display = 'block';
  }

  showResetButton(show = true) {
    const resetBtn = this.metaobjectsListElement.querySelector('.reset-button');
    if (resetBtn) {
      resetBtn.style.display = show ? 'block' : 'none';
    }
  }

  resetToAllLocations() {
    this.userLocation = null;
    this.currentZipCode = '';
    
    // Reset UI
    const zipInput = this.metaobjectsListElement.querySelector('.zip-input');
    if (zipInput) {
      zipInput.value = '';
      zipInput.classList.remove('invalid');
    }
    
    // Hide status
    this.metaobjectsListElement.querySelector('.status-display').style.display = 'none';
    this.metaobjectsListElement.querySelector('.location-info').style.display = 'none';
    this.showResetButton(false);
    
    // Remove distance badges
    this.metaobjectsListElement.querySelectorAll('.distance-badge').forEach(badge => badge.remove());
    
    // Show all locations
    const customElement = this.metaobjectsListElement;
    if (customElement.filteredMetaobjects !== undefined) {
      customElement.filteredMetaobjects = [...this.allLocations];
    }
    
    if (typeof customElement.renderMetaobjects === 'function') {
      customElement.renderMetaobjects(this.allLocations);
    } else {
      const allItems = this.metaobjectsListElement.querySelectorAll('.metaobject-item');
      allItems.forEach(item => item.style.display = 'block');
    }

    // Re-apply any existing search/filters
    if (typeof customElement.applyFiltersAndSearch === 'function') {
      customElement.applyFiltersAndSearch();
    }
    
    // Re-hide coordinate fields after reset
    setTimeout(() => this.hideCoordinateFields(), 100);
  }

  hideCoordinateFields() {
    // Hide latitude and longitude fields from being displayed to users
    // These are only needed for the store locator functionality
    const allItems = this.metaobjectsListElement.querySelectorAll('.metaobject-item');
    
    allItems.forEach(item => {
      // Find and hide latitude/longitude field displays
      const fields = item.querySelectorAll('.metaobject-field');
      
      fields.forEach(field => {
        const label = field.querySelector('.metaobject-field-label');
        if (label) {
          const labelText = label.textContent.toLowerCase();
          if (labelText.includes('latitude') || labelText.includes('longitude')) {
            // Hide the field and add CSS class for backup targeting
            field.style.display = 'none';
            field.classList.add('coordinate-field');
          }
        }
      });
    });
    
  }

  hookIntoRenderSystem() {
    // This method can be used to hook into the existing render system if needed
    // For now, we'll work with the DOM directly
  }
}

// Auto-initialize when script loads (prevent duplicate initialization)
if (!window.standaloneStoreLocator) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      if (!window.standaloneStoreLocator) {
        window.standaloneStoreLocator = new StandaloneStoreLocator();
      }
    });
  } else {
    window.standaloneStoreLocator = new StandaloneStoreLocator();
  }
}
